import { Loader } from "@/components/Common/Loader/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { AnalyticsEventType } from "@/constants/analytics";
// import env from "@/env";
import { useNotificationStore } from "@/store/notification";
import { trackEvent } from "@/utils/analytics";
import { emailSchema, REQUIRED_MESSAGE } from "@/utils/form";
import { sendCode } from "@/utils/passwordless";
import { Formik } from "formik";
import * as Yup from "yup";

type OTPEmailStepProps = {
  hasBackButton?: boolean;
  onCancel: () => void;
  onComplete?: (options: { email: string }) => void;
};

export const OTPEmailStep = ({ onComplete, onCancel, hasBackButton }: OTPEmailStepProps) => {
  const displayError = useNotificationStore((state) => state.displayError);
  const { toast } = useToast();

  return (
    <Formik<OTPEmailFormType>
      initialValues={{
        email: ""
      }}
      validationSchema={OTPEmailFormTypeFormSchema}
      onSubmit={async ({ email }) => {
        try {
          // if (env.ENVIRONMENT === "production") {
          await sendCode({ email });
          // }

          toast({ title: "Email sent. Please check your inbox.", variant: "success" });

          trackEvent(AnalyticsEventType.OTP_REQUESTED_CODE_COMPLETED);

          onComplete?.({ email });
        } catch (e) {
          trackEvent(AnalyticsEventType.OTP_REQUESTED_CODE_FAILED);

          displayError({
            title: "ERROR",
            message: (e as Error).message || "Failed to send code",
            isPublic: true
          });
        }
      }}
      validateOnMount={true}
    >
      {({
        handleSubmit,
        handleBlur,
        handleChange,
        values,
        isValid,
        isSubmitting,
        touched,
        errors
      }) => {
        return (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label>
                Enter Email
              </Label>
              <Input
                name="email"
                value={values.email}
                invalid={touched.email && !!errors.email}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              {touched.email && errors.email && (
                <div className="subtext-small capitalize text-red-light-500">{errors.email}</div>
              )}
            </div>

            <div className="flex flex-row items-center gap-3">
              {hasBackButton && <Button
                type="button"
                variant={"outline"}
                className="gap-3 bg-gray-dark-600 flex-1"
                onClick={onCancel}
              >
                <span>Go back</span>
              </Button>}
              <Button
                type="submit"
                variant={"outline"}
                className="gap-3 bg-gray-dark-600 flex-1"
                disabled={!isValid || isSubmitting}
              >
                <span>Continue</span>
                {isSubmitting && <Loader className="!h-[14px] !w-[14px]" color="#fff" />}
              </Button>
            </div>

            {/* <p className="subtext-small text-gray-dark-1000">
                        <a
                            onClick={onCancel}
                            className="cursor-pointer text-white hover:underline"
                        >
                            Cancel
                        </a>
                    </p> */}
          </form>
        );
      }}
    </Formik>
  );
};

export type OTPEmailFormType = {
  email: string;
};

const OTPEmailFormTypeFormSchema = Yup.object().shape({
  email: emailSchema().required(REQUIRED_MESSAGE)
});
